import { FormManager } from '@feature/dynamic_form/form_manager/form_manager.service';
import { FileManagerService } from '@feature/file_manager/mod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BasketParticipantApiModel, MessagingBasketProductApiModel } from '@visiba/api-vcs-client-v2';
import {
	basketAddMessagingProductMutation,
	basketDeleteActiveBasketMutation,
	basketDeselectParticipantMutation,
	basketGetActiveBasketOptions,
	basketGetBasketOptions,
	basketRemoveItemsMutation,
	basketSelectParticipantMutation,
	FormOpportunity,
	TextFormat,
} from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react';

export interface Basket extends MessagingBasketProductApiModel {
	Id: number;
	ProductId: number;
}

export interface BasketHook {
	receptionId: Nullable<number>;
	readonly basketId: Nullable<number>;
	readonly basket: Nullable<Basket>;
	readonly reasonForVisitId: Nullable<number> | undefined;
	readonly formManager: FormManager;
	readonly fileManagerService: FileManagerService;

	addData(data: {
		reasonForVisitId: number | null;
		redRobinSessionId?: string | null;
		asAgentFor?: string | null;
		informationGatheringSubmissionId?: string;
	}): Promise<void>;
	deleteActiveBasket(receptionId?: number): Promise<void>;
	selectParticipants(participantIds: number[]): Promise<void>;
	deselectParticipants(participantIds: number[]): Promise<void>;
	restore(receptionId: number, participants?: BasketParticipantApiModel[]): Promise<number>;
}

interface BasketState {
	receptionId: Nullable<number>;
	basketId: Nullable<number>;
	basket: Nullable<Basket>;
	reasonForVisitId: Nullable<number> | undefined;
}

const BasketContext = createContext<BasketHook | null>(null);

export function useBasket() {
	const context = useContext(BasketContext);
	if (context === null) {
		throw new Error('useBasket must be used within a <BasketProvider>');
	}
	return context;
}

export function BasketProvider({ children }: PropsWithChildren) {
	const queryClient = useQueryClient();
	const formManager = useResolve(FormManager);
	const fileManagerService = useResolve(FileManagerService);
	const loggerRef = useRef(new Logger('useBasket'));

	const deleteActiveBasketMutation = useMutation(basketDeleteActiveBasketMutation());
	const removeItemsMutation = useMutation(basketRemoveItemsMutation());
	const addMessagingProductMutation = useMutation(basketAddMessagingProductMutation());
	const selectParticipantMutation = useMutation(basketSelectParticipantMutation());
	const deselectParticipantMutation = useMutation(basketDeselectParticipantMutation());

	// Refs for mutation functions to avoid unstable useMutation return objects in useCallback deps
	const deleteActiveBasketMutateRef = useRef(deleteActiveBasketMutation.mutateAsync);
	deleteActiveBasketMutateRef.current = deleteActiveBasketMutation.mutateAsync;
	const removeItemsMutateRef = useRef(removeItemsMutation.mutateAsync);
	removeItemsMutateRef.current = removeItemsMutation.mutateAsync;
	const addMessagingProductMutateRef = useRef(addMessagingProductMutation.mutateAsync);
	addMessagingProductMutateRef.current = addMessagingProductMutation.mutateAsync;
	const selectParticipantMutateRef = useRef(selectParticipantMutation.mutateAsync);
	selectParticipantMutateRef.current = selectParticipantMutation.mutateAsync;
	const deselectParticipantMutateRef = useRef(deselectParticipantMutation.mutateAsync);
	deselectParticipantMutateRef.current = deselectParticipantMutation.mutateAsync;

	const [{ receptionId, basketId, basket, reasonForVisitId }, setBasketState] = useState<BasketState>({
		receptionId: null,
		basketId: null,
		basket: null,
		reasonForVisitId: undefined,
	});

	// Ref for receptionId to avoid unstable deps in useCallback
	const receptionIdRef = useRef(receptionId);
	receptionIdRef.current = receptionId;

	const fetchActiveBasketId = useCallback(
		async (receptionId: number) => {
			const response = await queryClient.fetchQuery(basketGetActiveBasketOptions({ path: { receptionId } }));
			loggerRef.current.logInformation('Set active basket id to {basketId}', response.Data);

			return response.Data;
		},
		[queryClient],
	);

	const populateBasket = useCallback(
		(id: number, basket: MessagingBasketProductApiModel, participants: BasketParticipantApiModel[] = basket.Participants ?? []) => {
			formManager.addRespondants(
				basket.Forms?.map((form) => ({
					completed: form.Answered,
					editable: true,
					formId: form.Id,
					header: form.Header ?? '',
					opportunity: FormOpportunity.BeforeCase,
					basketId: id,
					basketProductId: basket.Id,
					issueTrackingId: '',
					dataProvider: { type: 'basket', basketId: id, basketProductId: basket.Id },
				})) ?? [],
			);

			return {
				...basket,
				Id: id,
				ProductId: basket.Id,
				Participants: participants,
			};
		},
		[formManager],
	);

	const restore = useCallback(
		async (receptionId: number, participants?: BasketParticipantApiModel[]) => {
			loggerRef.current.logInformation('Restoring basket at receptionId: {receptionId}', receptionId);

			const activeBasketId = await fetchActiveBasketId(receptionId);

			loggerRef.current.logInformation('Basket id found, fetching basket products');

			const getBasketResult = await queryClient.fetchQuery(basketGetBasketOptions({ path: { basketId: activeBasketId } }));
			const basketProducts = getBasketResult.Data?.MessagingProducts ?? [];

			if (basketProducts.length > 0) {
				const basketProduct = basketProducts[0];
				const populateResult = populateBasket(
					activeBasketId,
					basketProduct,
					participants != null && participants.length > 0 ? participants : basketProduct.Participants,
				);

				setBasketState({
					receptionId,
					basketId: activeBasketId,
					basket: populateResult,
					reasonForVisitId: basketProduct.ReceptionConditionId,
				});
			} else {
				setBasketState({
					receptionId,
					basketId: activeBasketId,
					basket: null,
					reasonForVisitId: undefined,
				});
			}

			return activeBasketId;
		},
		[queryClient, fetchActiveBasketId, populateBasket],
	);

	const addData = useCallback(
		async (data: {
			reasonForVisitId: number | null;
			redRobinSessionId?: string | null;
			asAgentFor?: string | null;
			informationGatheringSubmissionId?: string;
		}) => {
			if (basketId == null) {
				throw new Error('BasketId is null. Run restore() before using this method.');
			}

			// If basket have a product, we need to delete it before adding new data.
			// It will reset reason for visit and forms.
			if (basket != null) {
				await removeItemsMutateRef.current({ path: { basketId, basketItemId: basket.ProductId } });
				formManager.removeForms();
			}

			const addMessagingProductResult = await addMessagingProductMutateRef.current({
				path: { basketId },
				body: {
					ReceptionConditionId: data.reasonForVisitId,
					ReasonFormat: TextFormat.Plaintext,
					AsAgentFor: data.asAgentFor,
					RedRobinSessionId: data.redRobinSessionId,
					InformationGatheringSubmissionId: data.informationGatheringSubmissionId,
				},
			});
			const participants = addMessagingProductResult.Data?.Participants ?? [];

			setBasketState((prev) => ({
				...prev,
				reasonForVisitId: data.reasonForVisitId ?? null,
			}));

			await restore(receptionId ?? -1, participants);

			return;
		},
		[basketId, basket, formManager, restore, receptionId],
	);

	const updateParticipantSelection = useCallback((participantIds: number[], selected: boolean) => {
		const participantIdSet = new Set(participantIds);

		setBasketState((prev) => {
			if (prev.basket == null || participantIdSet.size === 0) {
				return prev;
			}

			return {
				...prev,
				basket: {
					...prev.basket,
					Participants: prev.basket.Participants.map((participant) =>
						participantIdSet.has(participant.Id) ? { ...participant, Selected: selected } : participant,
					),
				},
			};
		});
	}, []);

	const selectParticipants = useCallback(
		async (participantIds: number[]) => {
			const uniqueParticipantIds = [...new Set(participantIds)];
			if (uniqueParticipantIds.length === 0) {
				return;
			}

			if (basketId == null || basket == null) {
				throw new Error('Basket is null. Run restore() before using this method.');
			}

			await Promise.all(
				uniqueParticipantIds.map((participantId) =>
					selectParticipantMutateRef.current({
						path: { basketId, basketProductId: basket.ProductId, participantId },
					}),
				),
			);

			updateParticipantSelection(uniqueParticipantIds, true);
		},
		[basketId, basket, updateParticipantSelection],
	);

	const deselectParticipants = useCallback(
		async (participantIds: number[]) => {
			const uniqueParticipantIds = [...new Set(participantIds)];
			if (uniqueParticipantIds.length === 0) {
				return;
			}

			if (basketId == null || basket == null) {
				throw new Error('Basket is null. Run restore() before using this method.');
			}

			await Promise.all(
				uniqueParticipantIds.map((participantId) =>
					deselectParticipantMutateRef.current({
						path: { basketId, basketProductId: basket.ProductId, participantId },
					}),
				),
			);

			updateParticipantSelection(uniqueParticipantIds, false);
		},
		[basketId, basket, updateParticipantSelection],
	);

	const deleteActiveBasket = useCallback(
		async (targetReceptionId = receptionIdRef.current ?? -1) => {
			loggerRef.current.logInformation('Clear basket');
			await deleteActiveBasketMutateRef.current({ path: { receptionId: targetReceptionId } });

			formManager.removeForms();
			setBasketState({
				receptionId: targetReceptionId === -1 ? null : targetReceptionId,
				basketId: null,
				basket: null,
				reasonForVisitId: undefined,
			});
		},
		[formManager],
	);

	const value: BasketHook = useMemo(
		() => ({
			receptionId,
			basketId,
			basket,
			reasonForVisitId,
			formManager,
			fileManagerService,
			addData,
			deleteActiveBasket,
			selectParticipants,
			deselectParticipants,
			restore,
		}),
		[
			receptionId,
			basketId,
			basket,
			reasonForVisitId,
			formManager,
			fileManagerService,
			addData,
			deleteActiveBasket,
			selectParticipants,
			deselectParticipants,
			restore,
		],
	);

	return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
}
